import { ReplenishmentCausa } from '../services/api';

export const PHYSICAL_STORES = ['A000', 'B000', 'C000', 'D000', 'E000', 'F000'];
export const ONLINE_STORE = 'T000';
export const ALL_STORES = [...PHYSICAL_STORES, ONLINE_STORE];
export const TOTAL = 'TOTAL';
export const STORE_NAMES: Record<string, string> = { F000: 'Gaia', [ONLINE_STORE]: 'Online' };

export const storeLabel = (code: string): string =>
  code === TOTAL ? 'Total lojas' : STORE_NAMES[code] ? `${code} · ${STORE_NAMES[code]}` : code;

export const fmtInt = (v: number): string => v.toLocaleString('pt-PT');
export const fmtPct = (v: number | null | undefined): string =>
  v === null || v === undefined
    ? '-'
    : `${v.toLocaleString('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
export const fmtEur = (v: number, digits = 0): string =>
  v.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: digits, minimumFractionDigits: digits });
export const fmtDay = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

// order_ts / snapshot_ts vêm como DATETIME (hora de Lisboa, sem fuso): formatar sem conversão.
export const fmtLocalDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} ${iso.slice(11, 16)}`;
};

export const DESTINOS: Record<string, { label: string; badge: string }> = {
  entregue: { label: 'Entregue', badge: 'bg-success' },
  cancelada: { label: 'Cancelada', badge: 'bg-secondary' },
  parcial: { label: 'Parcial', badge: 'bg-info text-dark' },
  pronta_nao_levantada: { label: 'Não levantada', badge: 'bg-warning text-dark' },
  aberta: { label: 'Aberta', badge: 'bg-primary' },
};
// Abertas / não levantadas com menos de 7 dias ainda estão em curso (não "paradas")
export const EM_CURSO_DIAS = 7;

export const ESTADOS_SAP: Record<string, string> = {
  Ini: 'Inicial', PagP: 'Pagamento pendente', Trat: 'Em tratamento', Prod: 'Aguardar produto',
  Leva: 'Pronta p/ levantamento', Env: 'Levantada/enviada', Canc: 'Cancelada',
};
export const estadoSap = (code: string | null | undefined): string =>
  code ? ESTADOS_SAP[code] || code : '-';

export const fmtHours = (h: number | null | undefined): string => {
  if (h === null || h === undefined) return '-';
  return h < 48 ? `${h.toLocaleString('pt-PT', { maximumFractionDigits: 1 })} h`
    : `${(h / 24).toLocaleString('pt-PT', { maximumFractionDigits: 1 })} d`;
};

export interface CausaMeta {
  label: string;
  hint: string;
  badge: string;   // classe bootstrap bg-*
}

export const CAUSAS: Record<ReplenishmentCausa, CausaMeta> = {
  ok: { label: 'OK', hint: 'Stock suficiente', badge: 'bg-success' },
  catalogacao: { label: 'Catalogação', hint: 'Só o T000 tem stock', badge: 'bg-warning text-dark' },
  distribuicao: { label: 'Distribuição', hint: 'Outras lojas têm, esta não', badge: 'bg-info text-dark' },
  quantidade: { label: 'Quantidade', hint: 'Stock insuficiente para a quantidade', badge: 'bg-secondary' },
  ruptura: { label: 'Ruptura', hint: 'Sem stock nas lojas nem no T000', badge: 'bg-danger' },
  ruptura_t: { label: 'Ruptura T', hint: 'T000 sem stock e nenhuma loja tem', badge: 'bg-danger' },
  stock_so_em_lojas: { label: 'Stock só em lojas', hint: 'T000 sem stock, mas há em lojas', badge: 'bg-info text-dark' },
  excluida_fornecedor: { label: 'Excl. fornecedor', hint: 'Linha N000 (stock de fornecedor) — fora do hit rate', badge: 'bg-dark' },
  acrescentada_loja: { label: 'Acrescentada na loja', hint: 'Linha criada no levantamento — não conta para o hit rate', badge: 'bg-secondary' },
  cancelada: { label: 'Cancelada', hint: 'Linha cancelada — não conta para o hit rate', badge: 'bg-secondary' },
  excluida_recondicionado: { label: 'Excl. recondicionado', hint: 'Linha R000 (recondicionados) — fora do hit rate', badge: 'bg-dark' },
};

// Causas de falha por tipo de loja (para o filtro e a legenda)
export const STORE_CAUSAS: ReplenishmentCausa[] = ['catalogacao', 'distribuicao', 'quantidade', 'ruptura'];
export const ONLINE_CAUSAS: ReplenishmentCausa[] = [
  'ruptura_t', 'stock_so_em_lojas', 'quantidade', 'excluida_fornecedor', 'excluida_recondicionado',
];

const lisbonParts = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('pt-PT', {
    timeZone: 'Europe/Lisbon', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { day: get('day'), month: get('month'), year: get('year'), hour: get('hour'), minute: get('minute') };
};

/** Instante absoluto (TIMESTAMP com fuso, ex.: `calculated_at`) -> "dd/mm/aaaa HH:MM" em hora de Lisboa. */
export const fmtGeneratedAt = (iso: string | null | undefined): string => {
  const p = iso ? lisbonParts(iso) : null;
  return p ? `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}` : '-';
};

/** Instante absoluto -> "HH:MM" em hora de Lisboa. */
export const fmtLisbonTime = (iso: string | null | undefined): string => {
  const p = iso ? lisbonParts(iso) : null;
  return p ? `${p.hour}:${p.minute}` : '-';
};
